import { refreshSession } from './authClient.js';

/**
 * Proactive pre-expiry silent refresh scheduler.
 *
 * The SPA's reactive path only refreshes on a 401 (apiFetch single-flight), so a
 * navigation right at the 15-minute access-token expiry could bounce an active
 * tenant to the login screen (22-UAT test 5). This scheduler closes that gap: it
 * times a silent refresh ~60s BEFORE the access token expires, keeping both the
 * in-memory access token AND the durable HttpOnly `qelm_refresh` cookie
 * continuously rotated during active use.
 *
 * Security invariants:
 *   - It reuses the EXISTING single-flight `refreshSession()` — it never opens a
 *     second concurrent refresh path. A replayed rotated token would trip SEC-03a
 *     reuse-detection and revoke the whole family (threat T-22-19-01).
 *   - It holds NO token. It only reads the in-memory `accessTokenExpiresIn`
 *     (via the injected getter) to time the existing rotation (threat T-22-19-02).
 */

/** Refresh this many seconds before the access token actually expires. */
const REFRESH_LEAD_SECONDS = 60;
/** Floor so a tiny/odd TTL still schedules a near-future refresh (never 0/negative). */
const MIN_DELAY_SECONDS = 5;

/**
 * Pure delay calculation: refresh ~60s before expiry, floored at 5s.
 *   computeRefreshDelayMs(900) === 840000
 *   computeRefreshDelayMs(65)  === 5000   (floor)
 *   computeRefreshDelayMs(30)  === 5000   (floor)
 */
export function computeRefreshDelayMs(expiresInSeconds: number): number {
  return Math.max(expiresInSeconds - REFRESH_LEAD_SECONDS, MIN_DELAY_SECONDS) * 1000;
}

export interface StartOptions {
  /** Reads the freshly-stored access-token lifetime (seconds) from the token store. */
  getExpiresInSeconds: () => number | null;
  /** Invoked when a scheduled refresh fails — routes to the unauthenticated/sign-in path. */
  onExpired: () => void;
}

let timer: ReturnType<typeof setTimeout> | null = null;

/** Cancel any pending refresh timer. Idempotent — never leaves a timer running. */
export function stop(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

/**
 * Start (or restart) the proactive refresh loop. Clears any prior timer first so
 * there is never more than one timer running.
 */
export function start(options: StartOptions): void {
  stop();
  schedule(options);
}

/** Arm the next single refresh from the currently-stored expiry. */
function schedule(options: StartOptions): void {
  const expiresIn = options.getExpiresInSeconds();
  // No known lifetime (e.g. cold reload before the first refresh) — nothing to
  // schedule; the reactive 401 path remains the backstop.
  if (expiresIn === null) return;
  timer = setTimeout(() => {
    void tick(options);
  }, computeRefreshDelayMs(expiresIn));
}

/** A scheduled tick: rotate via the existing single-flight refresh, then reschedule. */
async function tick(options: StartOptions): Promise<void> {
  const ok = await refreshSession();
  if (!ok) {
    stop();
    options.onExpired();
    return;
  }
  // Reschedule from the freshly-stored `accessTokenExpiresIn` (rotated in place).
  schedule(options);
}
